import { merge } from "es-toolkit/compat";
import title from "title";
import titlecase from "titlecase";

/**
 * Title cases a given string. Use when you need to follow grammatical rules
 * closely. Intent "UI" is for application-defined strings which will treat
 * snake and kebab-cased strings and also follow
 * {@link https://www.chicagomanualofstyle.org/home.html | The Chicago Manual of Style}
 * with a specific exception list that is displayed as defined. Intent "content"
 * is great for user-defined content where the string may contain unintentional
 * mixed casing, which will be treated as intentional.
 *
 * @example txt
 * "this is neat -> This Is Neat"
 *
 * @example
 * "the state-of-the-art design -> The State-of-the-Art Design"
 *
 * @example
 * "this vs that -> This vs That"
 *
 * for more examples, see the test cases.
 *
 * @param string the string to title case
 * @returns a title cased string
 */
export const toTitleCaseString = (
  string = "",
  opts?:
    | { intent: "ui"; exceptions?: ExceptionsType[] | "all" }
    | { intent: "content"; lax?: boolean }
): string => {
  const actualOpts = merge(
    { intent: "ui", lax: false, exceptions: "all" },
    opts
  );

  switch (actualOpts.intent) {
    case "content": {
      return actualOpts.lax
        ? titlecase.toLaxTitleCase(string)
        : titlecase(string);
    }
    case "ui":
    default: {
      const isSnakeCase = new RegExp(
        /^[a-zA-Z][a-zA-Z0-9]*(?:_[a-zA-Z0-9]+)*$/
      ).test(string);
      const isKebabCase = new RegExp(
        /^[a-zA-Z][a-zA-Z0-9]*(?:-[a-zA-Z0-9]+)*$/
      ).test(string);

      // @ts-expect-error - handle CJS interop where default export is in .default
      const titleFn = title.default ?? title;
      return titleFn(
        isSnakeCase
          ? string.replace(/_/g, " ")
          : isKebabCase
            ? string.replace(/-/g, " ")
            : string,
        {
          special: getExceptionsList(actualOpts.exceptions),
        }
      );
    }
  }
};

// oil and gas industry specific
const exceptionsOilAndGas = [
  "JJJJ",
  "OOOOa",
  "OOOOb",
  "LDAR",
  "SubpartW",
  "TIER",
];

// conjunctions and prepositions
const exceptionsGrammar = [
  "is",
  "a",
  "an",
  "and",
  "as",
  "at",
  "because",
  "but",
  "by",
  "en",
  "for",
  "if",
  "in",
  "neither",
  "nor",
  "of",
  "on",
  "only",
  "or",
  "over",
  "per",
  "so",
  "some",
  "than",
  "that",
  "to",
  "up",
  "upon",
  "when",
  "with",
  "without",
  "yet",
  "v",
  "versus",
  "via",
  "vs",
];

const ExceptionsListMap = {
  "oil-and-gas": exceptionsOilAndGas,
  grammar: exceptionsGrammar,
} as const;
type ExceptionsType = keyof typeof ExceptionsListMap;

function getExceptionsList(config: ExceptionsType[] | "all") {
  if (config === "all") {
    return Object.values(ExceptionsListMap).flatMap((l) => l);
  } else {
    return config.flatMap((c) => {
      return ExceptionsListMap[c] ?? [];
    });
  }
}
